"use client";

import Image from "next/image";
import Link from "next/link";
import React from "react";

interface NewsPrCardProps {
  image: string;
  date: string;
  category: string;
  title: string;
  excerpt: string;
  slug?: string;
  postType?: 'News' | 'Press Release';
}

const NewsPrCard: React.FC<NewsPrCardProps> = ({
  image,
  date,
  category,
  title,
  excerpt,
  slug = "news-pr-post",
  postType = 'News',
}) => {
  return (
    <Link href={`/news-pr/${encodeURIComponent(slug || title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''))}`} className="block group">
      <div className="w-full bg-transparent overflow-hidden transition-all duration-300  hover:shadow-xl">
        {/* Top Image */}
        <div className="relative w-full aspect-video overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
          {/* Content Type Badge */}
          <div className={`absolute top-3 left-3 z-10 px-3 py-1.5 rounded-md text-xs font-semibold shadow-lg backdrop-blur-sm ${postType === 'Press Release'
              ? 'bg-purple-600/90 text-white border border-purple-400/50'
              : 'bg-blue-600/90 text-white border border-blue-400/50'
            }`}>
            {postType === 'Press Release' ? '📄 PR' : '📰 News'}
          </div>

          {image && image.trim() !== '' ? (
            <Image
              src={image}
              alt="News & PR thumbnail"
              fill
              className="object-fill sm:object-cover transition-transform duration-500 "
              quality={100}
              onError={(e) => {
                // Replace with fallback placeholder on error
                const target = e.currentTarget as HTMLImageElement;
                target.style.display = 'none';
                const container = target.parentElement;
                if (container) {
                  container.innerHTML = `
                    <div class="w-full h-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                      <div class="text-center">
                        <div class="text-4xl font-bold text-blue-600 mb-2">${title.charAt(0).toUpperCase()}</div>
                        <div class="text-xs text-blue-500 font-medium">News & PR</div>
                      </div>
                    </div>
                  `;
                }
              }}
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">{title.charAt(0).toUpperCase()}</div>
                <div className="text-xs text-blue-500 font-medium">News & PR</div>
              </div>
            </div>
          )}
          {/* Overlay effect */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300"></div>
        </div>

        {/* Content Card */}
        <div className="sm:m-4 m-2">
          <div className="bg-white/80 backdrop-blur-md px-3 sm:px-6 py-3 sm:py-4 text-[#333] -mt-16 relative z-50 shadow-[0_4px_8px_0_rgba(0,0,0,0.03)] transition-all duration-300 group-hover:bg-white/90 group-hover:shadow-[0_8px_25px_0_rgba(0,0,0,0.1)]">
            {/* Date & Category */}
            <div className="flex justify-between items-center mb-2">
              <span className="font-semibold text-[#1b1b1b] text-sm lg:text-base transition-colors duration-300 group-hover:text-[#272D57]">{date}</span>
              <span className="text-[#5060A1] text-sm lg:text-base font-bold transition-all duration-300 group-hover:text-[#E0301E] group-hover:scale-105">{category}</span>
            </div>

            {/* Title */}
            <h3 className="text-[#272D57] text-base sm:text-md lg:text-xl line-clamp-2 font-semibold leading-tight mb-2 transition-all duration-300 group-hover:text-[#E0301E]">
              {title}
            </h3>

            {/* Excerpt */}
            <p className="text-[#1b1b1b] leading-relaxed leading-snug text-sm lg:text-base transition-colors duration-300 group-hover:text-[#333] line-clamp-2">{excerpt}</p>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default NewsPrCard;
